import { HTTPStatuses, type HttpError } from 'constants/http';
import {
  FETCH_TRACKING_PAGE_FAILURE,
  FETCH_TRACKING_PAGE_REQUEST,
  FETCH_TRACKING_PAGE_SUCCESS,
  FETCH_TRACKING_PAGE_SUCCESS_WITHOUT_RESULTS,
} from 'constants/payment';
import {
  FETCH_TRACKING_SETUP_DATA_FAILURE,
  FETCH_TRACKING_SETUP_DATA_REQUEST,
  FETCH_TRACKING_SETUP_DATA_SUCCESS,
} from 'constants/trackingSetup';
import { getPayment, getPaymentId } from 'selectors/entities/payment/payment';
import { getCurrentLocale, isFetching } from 'selectors/ui/ui';
import type { PayexThunkAction } from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithStatus } from 'utils/errors/errors';

export const fetchDocuments =
  (): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { payex }) => {
    const state = getState();
    const isFetchingDocuments = isFetching(state, 'documents');
    const paymentId = getPaymentId(state);

    if (!paymentId || isFetchingDocuments) {
      return;
    }
    const { token } = getPayment(state);
    if (!token) {
      return;
    }

    dispatch({ type: FETCH_TRACKING_PAGE_REQUEST });

    const locale = getCurrentLocale(state);
    try {
      const response = await payex.trackingPage.fetch({
        paymentId,
        token,
        locale,
      });

      return dispatch({
        type: FETCH_TRACKING_PAGE_SUCCESS,
        payload: response,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status } = error as HttpError;

        if (status === HTTPStatuses.NOT_FOUND) {
          return dispatch({
            type: FETCH_TRACKING_PAGE_SUCCESS_WITHOUT_RESULTS,
          });
        }
      }

      errorNotifier.notify(error);

      return dispatch({ type: FETCH_TRACKING_PAGE_FAILURE });
    }
  };

export const fetchTrackingSetupData =
  (
    orderId: string,
    orderToken: string,
  ): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { payex }) => {
    const state = getState();
    const isFetchingData = isFetching(state, 'trackingSetupData');

    if (isFetchingData) {
      return;
    }

    dispatch({ type: FETCH_TRACKING_SETUP_DATA_REQUEST });

    try {
      const response = await payex.trackingPage.data(orderId, orderToken);

      dispatch({
        type: FETCH_TRACKING_SETUP_DATA_SUCCESS,
        payload: response,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const _error = error as HttpError;
        const { status = '' } = _error;
        if (status !== HTTPStatuses.NOT_FOUND) {
          _error.message = `${FETCH_TRACKING_SETUP_DATA_FAILURE} ${_error.message}`;
          errorNotifier.notifyWithFingerprint(_error, [
            FETCH_TRACKING_SETUP_DATA_FAILURE,
            `${status}`,
          ]);
        }
      }

      return dispatch({
        type: FETCH_TRACKING_SETUP_DATA_FAILURE,
      });
    }
  };
